/**
 * Bundle Metafield Variant Radios Web Component Coordinator
 * Main component that coordinates all bundle metafield functionality
 */
import { BundleAvailability } from './bundle-metafield-availability.js'
import { BundleCart } from './bundle-metafield-cart.js'
import { BundleDisplay } from './bundle-metafield-display.js'
import { BundleUtils } from './bundle-metafield-utils.js'

class BundleMetafieldVariantRadios extends HTMLElement {
  constructor() {
    super()

    // Check for debug flag from Liquid
    this.showDebug =
      this.dataset.showDebug === 'true' ||
      this.closest('[data-show-debug="true"]') !== null

    // Initialize modules
    this.utils = new BundleUtils(this.showDebug)
    this.availability = new BundleAvailability(this.utils)
    this.display = new BundleDisplay(this.utils)
    this.cart = new BundleCart(this.utils, this.display)

    this.utils.debug('BundleMetafieldVariantRadios component loaded')
    this.utils.debug('Initializing BundleMetafieldVariantRadios component')

    // Set up event listeners
    this.addEventListener('change', this.handleChange.bind(this))
    this.addEventListener('click', this.handleClick.bind(this))
    this.addEventListener('addToCartError', this.handleError.bind(this))

    // Initialize bundle data from component attributes
    this.bundleData = {
      parentVariantId: this.dataset.parentVariantId || '',
      discount: this.dataset.bundleDiscount || '0',
      hmacKey: this.dataset.hmacKey || '',
    }

    // Initialize quantity and state
    this.bundleQuantity = 1
    this.initialized = false

    // Initialize component
    this.initializeComponent()
    this.handleBundleAdded = this.handleBundleAdded.bind(this)
  }

  connectedCallback() {
    this.addEventListener('cart:bundle-added', this.handleBundleAdded)
  }

  disconnectedCallback() {
    this.removeEventListener('cart:bundle-added', this.handleBundleAdded)
  }

  /**
   * Initialize component with proper DOM ready detection
   */
  initializeComponent() {
    // Check if DOM is already ready
    if (document.readyState === 'loading') {
      // DOM is still loading, wait for it
      document.addEventListener('DOMContentLoaded', () => {
        this.performInitialization()
      })
    } else {
      // DOM is already loaded, initialize immediately
      this.performInitialization()
    }
  }

  /**
   * Perform the actual initialization
   */
  performInitialization() {
    if (this.initialized) return

    this.utils.debug('Bundle component initializing...')

    // Log debug info
    if (window.bundleDebugLogger) {
      window.bundleDebugLogger.log(
        'events',
        'Bundle component initialization started'
      )
    }

    // Initialize all components
    this.display.getCartComponents().then(() => {
      this.initializeQuantityIntegration()
      this.availability.initializeAvailability(this)

      this.display.updateBundleTotal(this, this.bundleQuantity)
      this.display.checkBundleAvailability(this)
    })
    this.display.updateDebugSelections(this)

    this.initialized = true
    this.utils.debug('Bundle component initialization complete')

    if (window.bundleDebugLogger) {
      window.bundleDebugLogger.log(
        'events',
        'Bundle component initialization completed'
      )
    }
  }

  /**
   * Handle radio button changes
   */
  async handleChange(event) {
    if (event.target.type !== 'radio') return

    this.utils.debug(
      'Radio change:',
      `${event.target.name} = ${event.target.value}`
    )

    // Log to debug system
    if (window.bundleDebugLogger) {
      window.bundleDebugLogger.log(
        'events',
        `Option changed: ${event.target.name} = ${event.target.value}`
      )
    }

    const groupElement = event.target.closest('[class*="bundle-group-"]')
    if (!groupElement) return

    // Update the legend to show selected value
    this.utils.updateLegendDisplay(event.target)

    // Check if this is a color change
    if (this.utils.isColorOption(event.target)) {
      await this.availability.handleColorChange(
        event.target.value,
        groupElement
      )
    }

    // Update display for any change
    this.display.getCartComponents().then(() => {
      this.display.updateGroupDisplay(groupElement, this)
      this.display.updateBundleTotal(this, this.bundleQuantity)
    })

    // Update selection tracking in debug
    if (window.bundleDebugLogger) {
      this.display.updateDebugSelections(this)
    }
  }

  /**
   * Handle cart errors
   */
  handleError(event) {
    this.displayError(event.detail?.errors?.message)
  }
  displayError(message) {
    if (!message) return
    const errorMessageWrapper = this.querySelector('[data-product-form-error]')
    if (!errorMessageWrapper) return

    const errorMessageEl = errorMessageWrapper.querySelector(
      '[data-product-form-error-message]'
    )
    if (!errorMessageEl) return

    errorMessageWrapper.setAttribute('aria-hidden', 'false')
    errorMessageEl.innerText = message
    setTimeout(() => {
      errorMessageWrapper.setAttribute('aria-hidden', 'true')
      errorMessageEl.innerText = ''
    }, 3000)
  }

  /**
   * Handle click events
   */
  handleClick(event) {
    // Image modal triggers
    if (event.target.closest('[data-bundle-image-trigger]')) {
      const trigger = event.target.closest('[data-bundle-image-trigger]')
      this.display.openModal(this, trigger.dataset.groupIndex)
      return
    }

    // Modal close buttons
    if (event.target.closest('[data-bundle-modal-close]')) {
      const button = event.target.closest('[data-bundle-modal-close]')
      this.display.closeModal(this, button.dataset.groupIndex)
      return
    }

    // Add to cart button
    if (event.target.closest('[data-bundle-add-to-cart]')) {
      this.utils.debug('Add to cart clicked')
      try {
        this.cart.handleAddToCart(this, this.bundleData, this.bundleQuantity)
      } catch {
        console.log('error test ----')
      }
      return
    }
  }

  handleBundleAdded() {
    this.display.getCartComponents().then(() => {
      this.display.checkBundleAvailability(this)
    })
  }

  /**
   * Handle quantity changes from +/- buttons
   */
  handleQuantityChange(change) {
    const quantityInput = this.querySelector('[data-bundle-quantity-input]')
    if (!quantityInput) return

    const currentQuantity = parseInt(quantityInput.value) || 1
    const newQuantity = Math.max(1, currentQuantity + change)

    quantityInput.value = newQuantity
    this.bundleQuantity = newQuantity
    this.display.updateBundleTotal(this, this.bundleQuantity)
    this.display.getCartComponents().then(() => {
      this.display.checkBundleAvailability(this)
    })
    this.utils.debug('Quantity updated via buttons:', this.bundleQuantity)
  }

  /**
   * Initialize quantity integration
   */
  initializeQuantityIntegration() {
    const quantityInput = this.querySelector('[data-bundle-quantity-input]')

    if (quantityInput) {
      this.bundleQuantity = parseInt(quantityInput.value) || 1

      quantityInput.addEventListener('change', (event) => {
        const newQuantity = parseInt(event.target.value)
        if (newQuantity >= 1) {
          this.bundleQuantity = newQuantity
          this.display.getCartComponents().then(() => {
            this.display.updateBundleTotal(this, this.bundleQuantity)
            this.display.checkBundleAvailability(this)
          })
          this.utils.debug('Quantity updated:', this.bundleQuantity)
        }
      })
    }
  }
}

// Register the custom element
customElements.define(
  'bundle-metafield-variant-radios',
  BundleMetafieldVariantRadios
)

// Log component loading
if (window.bundleDebugLogger || console) {
  console.log('BundleMetafieldVariantRadios main component loaded')
}
